export type ShareInviteParseResult =
  | { ok: true; token: string; ownerId?: string; albumId?: string }
  | { ok: false; reason: "empty" | "guid_only" | "missing_token" | "unrecognized" };

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const URL_CANDIDATE_RE =
  /(?:https?:\/\/[^\s<>"'\\]+|joi\.pho:\/\/[^\s<>"'\\]+|\/owners\/[^\s<>"'\\]+)/gi;

export function shareInviteErrorMessage(reason: Exclude<ShareInviteParseResult, { ok: true }>["reason"]): string {
  switch (reason) {
    case "empty":
      return "Paste the invite link you received.";
    case "guid_only":
    case "missing_token":
      return "We need the full invite link, including the secret token. Long-press the message and tap Copy.";
    case "unrecognized":
    default:
      return "That doesn't look like a joint.photos invite link.";
  }
}

export function extractShareInvite(raw: string): ShareInviteParseResult {
  const text = raw.trim();
  if (!text) {
    return { ok: false, reason: "empty" };
  }

  if (UUID_RE.test(text)) {
    return { ok: false, reason: "guid_only" };
  }

  for (const candidate of collectUrlCandidates(text)) {
    const parsed = parseShareLinkUrl(candidate);
    if (parsed?.token) {
      return {
        ok: true,
        token: parsed.token,
        ownerId: parsed.ownerId,
        albumId: parsed.albumId,
      };
    }
    if (parsed && !parsed.token) {
      return { ok: false, reason: "missing_token" };
    }
  }

  return { ok: false, reason: "unrecognized" };
}

function collectUrlCandidates(text: string): string[] {
  const found: string[] = [];
  const matches = text.match(URL_CANDIDATE_RE) ?? [];
  for (const match of matches) {
    found.push(stripTrailingPunctuation(match));
  }
  found.push(stripTrailingPunctuation(text));
  return [...new Set(found.filter(Boolean))];
}

function stripTrailingPunctuation(value: string): string {
  return value.replace(/[)\].,;!?]+$/g, "");
}

/**
 * Parses share URLs from either:
 * - Custom scheme: joi.pho://share/album?ownerId=&albumId=&token=&webFallback=
 * - Web / universal: https://host/owners/:ownerId/albums/:albumId/link?token=
 * - Path-only: /owners/:ownerId/albums/:albumId/link?token=
 */
export function parseShareLinkUrl(
  url: string
): { ownerId?: string; albumId?: string; token?: string } | null {
  try {
    const normalized = url.includes("://") ? url : `https://dummy/${url.replace(/^\//, "")}`;
    const parsed = new URL(normalized);
    const token = parsed.searchParams.get("token") || undefined;
    const path = parsed.pathname.replace(/^\//, "");

    const webMatch = path.match(/^owners\/([^/]+)\/albums\/([^/]+)\/link\/?$/i);
    if (webMatch) {
      return {
        ownerId: decodeURIComponent(webMatch[1]),
        albumId: decodeURIComponent(webMatch[2]),
        token,
      };
    }

    const isCustomShare =
      (parsed.protocol === "joi.pho:" && parsed.hostname === "share" && path === "album") ||
      path === "share/album" ||
      path.endsWith("/share/album");

    if (isCustomShare) {
      return {
        ownerId: parsed.searchParams.get("ownerId") || undefined,
        albumId: parsed.searchParams.get("albumId") || undefined,
        token,
      };
    }

    return null;
  } catch {
    return null;
  }
}
